import React from "react";
import Text from "../../../components/Text/Text";
import { Badge, Col, Divider, Image, Row, Table } from "antd";
import { DetailBox, FlexCol, FlexRow } from "../../../components/Container/Container";
import icon from "../../../resource/icon";
import TablePagination from "../../../components/Table/TablePagination";
import type { ProductEntity } from "../../../entities/PoductEntity";
import { color, image } from "../../../resource";
import { numberFormatter, priceFormatter } from "../../../utility/Formatter";
import dayjs from "dayjs";
import { DetailItem } from "../../OrderManagement/orderDetail";
import type { OrderEntity } from "../../../entities/OrderEntity";

function OrderDetailLayout({ orderProduct }: { orderProduct?: OrderEntity | null }) {
  const columns = [
    {
      title: "ชื่อสินค้า",
      dataIndex: "productName",
      key: "productName",
      width: 360,
      render: (productName: string, row: ProductEntity, index: number) => {
        return {
          children: (
            <FlexRow align='center'>
              <div style={{ marginRight: 16 }}>
                <Image
                  src={row?.productImage || image.emptyProductBrand}
                  style={{
                    width: "55px",
                    height: "55px",
                    objectFit: "contain",
                  }}
                />
              </div>
              <FlexCol>
                <Text level={5}>{productName}</Text>
                <Text level={6} color='Text3'>
                  {row?.commonName}
                </Text>
              </FlexCol>
            </FlexRow>
          ),
        };
      },
    },
    {
      title: "ขนาด",
      dataIndex: "packSize",
      key: "packSize",
      width: 170,
      render: (packSize: string, product: ProductEntity, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text level={5}>{"MT"}</Text>
              <Text level={6} color='Text3'>
                {product?.productCodeNAV || product?.productFreebiesCodeNAV}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "จำนวน",
      dataIndex: "quantity",
      key: "quantity",
      width: 125,
      render: (quantity: string, product: ProductEntity, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text level={5}>{Number(quantity).toFixed(2)}</Text>
              <Text level={6} color='Text3'>
                {product?.baseUnitOfMeaTh || product?.saleUOMTH || product?.saleUOM}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ราคา/หน่วย",
      dataIndex: "marketPrice",
      key: "marketPrice",
      width: 160,
      render: (marketPrice: number, product: ProductEntity, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text level={5}>{priceFormatter(marketPrice || "0", undefined, false, true)}</Text>
              <Text level={6} color='Text3'>
                {" บาท / " +
                  (product?.saleUOMTH || product?.saleUOM || product?.saleUom || "หน่วย")}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "โปรโมชัน",
      dataIndex: "orderProductPromotions",
      key: "orderProductPromotions",
      width: 160,
      render: (orderProductPromotions: any[], product: any, index: number) => {
        const findPromotion = () => {
          let promotion = "";
          if (product?.orderProductPromotions?.length) {
            const p: any = [];
            for (let i = 0; product?.orderProductPromotions.length > i; i++) {
              p.push(product?.orderProductPromotions[i].promotionCode);
            }
            promotion = p.map((a: any) => a).join(", ");
          } else {
            promotion = product.productPromotionCode;
          }
          return promotion || "-";
        };
        return {
          children: (
            <FlexCol>
              <Text key={index} level={5}>
                {findPromotion()}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "วันกำหนดชำระ",
      dataIndex: "dueDate",
      key: "dueDate",
      width: 160,
      render: (dueDate: number, product: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text level={5}>
                {dueDate ? dayjs(dueDate).locale("th").format("D MMM BBBB") : "-"}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ส่วนลดรายการ/หน่วย",
      dataIndex: "discount",
      key: "discountPerUnit",
      width: 160,
      render: (discount: number, product: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text level={5} color={discount ? "error" : "Text3"} fontWeight={700}>
                {discount
                  ? "- " +
                    priceFormatter(discount / product.quantity || "0", undefined, false, true)
                  : "-"}
              </Text>
              <Text level={6} color='Text3'>
                บาท
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ส่วนลดโควตา",
      title2: "/หน่วย",
      dataIndex: "quotaDiscount",
      key: "quotaDiscount",
      width: 150,
      render: (discount: number, product: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text level={5} color={discount ? "error" : "Text3"} fontWeight={700}>
                {discount
                  ? "- " +
                    priceFormatter(discount / product.quantity || "0", undefined, false, true)
                  : "-"}
              </Text>
              <Text level={6} color='Text3'>
                บาท
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ส่วนลด CO/หน่วย",
      dataIndex: "coDiscount",
      key: "discountPerUnit",
      width: 150,
      render: (discount: number, product: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text
                level={5}
                color={discount || product.coDiscount ? "error" : "Text3"}
                fontWeight={700}
              >
                {product.coDiscount
                  ? "- " +
                    priceFormatter(
                      product.coDiscount / product.quantity || "0",
                      undefined,
                      false,
                      true,
                    )
                  : "-"}
              </Text>
              <Text level={6} color='Text3'>
                บาท
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ส่วนลดรวม",
      dataIndex: "discount",
      key: "discount",
      width: 160,
      fixed: "right",
      render: (discount: number, product: any, index: number) => {
        const totalDiscount = discount + product.coDiscount + (product.quotaDiscount || 0);
        return {
          children: (
            <FlexCol>
              <Text level={5} color={totalDiscount ? "error" : "Text3"} fontWeight={700}>
                {totalDiscount
                  ? "- " + priceFormatter(totalDiscount || "0", undefined, false, true)
                  : "-"}
              </Text>
              <Text level={6} color='Text3'>
                บาท
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "Special REQ./หน่วย",
      dataIndex: "specialRequestDiscount",
      key: "specialRequestDiscountPerUnit",
      width: 160,
      fixed: "right",
      render: (discount: number, product: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text level={5} color={discount ? "purple" : "Text3"} fontWeight={700}>
                {discount
                  ? "- " +
                    priceFormatter(discount / product.quantity || "0", undefined, false, true)
                  : "-"}
              </Text>
              <Text level={6} color='Text3'>
                บาท
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "Special REQ.รวม",
      bottomSize: 16,
      dataIndex: "specialRequestDiscount",
      key: "specialRequestDiscount",
      fixed: "right",
      width: 160,
      render: (discount: number, product: ProductEntity, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text level={5} color={discount ? "purple" : "Text3"} fontWeight={700}>
                {discount ? "- " + priceFormatter(discount || "0", undefined, false, true) : "-"}
              </Text>
              <Text level={6} color='Text3'>
                บาท
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ราคารวมก่อนหักส่วนลด",
      bottomSize: 16,
      dataIndex: "price",
      key: "price",
      width: 180,
      fixed: "right",
      render: (price: number, product: ProductEntity, index: number) => {
        return {
          children: (
            <>
              {product.isFreebie && !product.isSpecialRequestFreebie && (
                <div style={{ position: "relative", bottom: 26, right: -10 }}>
                  <Badge.Ribbon text='ของแถม' placement='end' />
                </div>
              )}
              {product.isFreebie && product.isSpecialRequestFreebie && (
                <div style={{ position: "relative", bottom: 26, right: -10 }}>
                  <Badge.Ribbon text='ของแถม' placement='end' color={color.error} />
                </div>
              )}
              <FlexCol>
                <Text level={5} color='primary' fontWeight={700}>
                  {priceFormatter(price || "0", undefined, false, true)}
                </Text>
                <Text level={6} color='Text3'>
                  {"บาท"}
                </Text>
              </FlexCol>
            </>
          ),
        };
      },
    },
  ];
  return (
    <div>
      <Text fontWeight={700} style={{ marginTop: 18, marginBottom: 18 }}>
        รายละเอียดคำสั่งซื้อ
      </Text>
      <Row style={{ paddingBottom: 16 }}>
        <Col span={12}>
          <FlexRow align='center'>
            <Text level={6}>
              ทั้งหมด&nbsp;{numberFormatter(orderProduct?.orderProducts?.length || 0, 0)}
              &nbsp;รายการ
            </Text>
          </FlexRow>
        </Col>
        <Col span={12}>
          <div style={{ display: "flex", justifyContent: "end", paddingRight: 8, gap: 8 }}>
            <img src={icon.ribbonBadgeBlue} width={20} /> <span>ของแถมโปรโมชั่น</span>
            <img src={icon.ribbonBadgeRed} width={20} /> <span>ของแถม Special Request</span>
          </div>
        </Col>
      </Row>
      <TablePagination
        isLoading={false}
        columns={columns as any}
        data={orderProduct?.orderProducts || []}
        scroll={{ x: "max-content", y: 400 }}
        locale={{
          emptyText: (
            <div
              style={{
                padding: 32,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 24,
                minHeight: 200,
              }}
            >
              <img
                src={icon.emptyProduct}
                style={{
                  width: 80,
                  height: 80,
                  objectFit: "contain",
                }}
              />
              <Text fontSize={16} color='Text3'>
                ไม่มีข้อมูลคำสั่งซื้อ
              </Text>
            </div>
          ),
        }}
      />
      <div
        style={{
          borderRadius: "10px",
          backgroundColor: color.background1,
          padding: "10px",
          border: `0.3px solid ${color.background2}`,
          margin: "16px 0 16px 0",
        }}
      >
        <Row justify={"space-between"}>
          <Col span={12}>
            <DetailItem
              label='รายการทั้งหมด :'
              value={`${numberFormatter(orderProduct?.orderProducts?.length || 0, 0)} รายการ`}
            />
            <DetailItem
              label='รายการของแถม :'
              value={`${numberFormatter(orderProduct?.orderProducts?.filter((p) => p.isFreebie).length || 0, 0)} รายการ`}
            />
          </Col>
          <Col span={12}>
            <DetailItem
              label='จำนวนสินค้าทั้งหมด'
              labelEn='รวมของแถม'
              value={
                <Text fontWeight={600} fontSize={16}>
                  {Object.entries(
                    orderProduct?.orderProducts?.reduce((acc: Record<string, number>, item) => {
                      const unit = item.saleUOMTH || item.saleUOM || item.saleUom || "";
                      if (unit) {
                        acc[unit] = (acc[unit] || 0) + Number(item.quantity);
                      }
                      return acc;
                    }, {}) ?? {},
                  )
                    .map(([unit, qty]) => `${qty} ${unit}`)
                    .join(", ")}
                </Text>
              }
            />
          </Col>
        </Row>
      </div>
      <DetailBox>
        <DetailItem
          label='ราคาก่อนลด'
          value={priceFormatter(
            (
              Number(orderProduct?.totalPrice || 0) + Number(orderProduct?.totalDiscount || 0)
            ).toString(),
            undefined,
            true,
          )}
          alignRight
          fontWeight={700}
          fontSize={18}
          leftSpan={10}
        />
        <DetailItem label='รายละเอียดส่วนลด' value=' ' fontWeight={700} fontSize={18} />
        <DetailBox style={{ backgroundColor: "white", padding: 22 }}>
          <DetailItem
            label='ส่วนลดรายการ'
            fontFamily='Sarabun'
            labelEn='Discount'
            value={priceFormatter(orderProduct?.discount || "0", undefined, true)}
            color='error'
            alignRight
            style={{ color: "#2ED477" }}
          />
          <DetailItem
            label='ส่วนลดพิเศษ'
            fontFamily='Sarabun'
            labelEn='Special Req.'
            value={priceFormatter(orderProduct?.specialRequestDiscount || "0", undefined, true)}
            style={{ color: "#9B51E0" }}
            alignRight
          />
          <DetailItem
            label='ส่วนลดเงินสด'
            fontFamily='Sarabun'
            labelEn='Cash'
            value={priceFormatter(orderProduct?.cashDiscount || "0", undefined, true)}
            color='secondary'
            style={{ color: "#FF9138" }}
            alignRight
          />
          <DetailItem
            label=' ส่วนลดดูแลราคา'
            labelEn='CO. ดูแลราคา / วงเงินเคลม'
            fontFamily='Sarabun'
            value={priceFormatter(orderProduct?.coDiscount || "0", undefined, true)}
            color='success'
            alignRight
            style={{ color: "#F46363" }}
          />
        </DetailBox>
        <br />
        <DetailItem
          label='รวมส่วนลดสุทธิ'
          value={priceFormatter(orderProduct?.totalDiscount || "0", undefined, true)}
          fontWeight={700}
          fontSize={18}
          alignRight
          leftSpan={10}
        />
        <DetailItem
          label={`มูลค่ารวมหลังหักส่วนลด`}
          value={priceFormatter(orderProduct?.totalPrice || "0", undefined, true)}
          fontWeight={700}
          fontSize={18}
          alignRight
          leftSpan={10}
        />
        {orderProduct?.orderProducts && orderProduct?.orderProducts?.length > 0 && (
          <DetailItem
            label={`ภาษีมูลค่าเพิ่ม 7%`}
            value={priceFormatter(orderProduct?.vat || "0", undefined, true)}
            fontWeight={700}
            fontSize={18}
            alignRight
            leftSpan={10}
          />
        )}
        <Divider />
        <DetailItem
          label='ราคารวมทั้งสิ้น'
          value={
            <Text color='primary' fontWeight={700} fontSize={32}>
              {priceFormatter(orderProduct?.totalPrice || "0", undefined, true)}
            </Text>
          }
          fontWeight={700}
          fontSize={24}
          alignRight
          leftSpan={10}
        />
      </DetailBox>
    </div>
  );
}

export default OrderDetailLayout;
